import { FC, RefObject, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { T } from 'src/shared/helpers';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { FrameSelectionIcon, FullscreenExitIcon, FullscreenIcon, ResetIcon } from 'src/components/widgets/icons/scene';
import { DropdownActionItem, DropdownContent, DropdownList, useDropdown } from '../../../../../widgets/dropdown';
import { ZTooltip } from '../../../../../widgets/ztooltip';
import useComposerStore from '../../configurator-composer/composer/composerStore';
import { ComposerAreaMark, ComposerOptionMark } from '../../configurator-composer/composer/components/composerAttributeTreeElement';
import { ToolButton, ToolGroup } from '../scene-editors/toolbar/SceneEditorToolbar';
import { requestComposerReveal } from './composerReveal';
import { ComposerMeshUser, getMeshUsers, getSelectionSceneRefs, hasSceneRefs, resolveSceneRefs } from './composerSceneTargets';

// I GESTI DIRETTI SUL 3D DEL COMPOSER (utente, 2026-10-15, punto 9). Il Composer non costruisce la
// scena — niente gizmo, griglia o barra di stato —, ma la guarda: «Inquadra» porta la camera sugli
// oggetti che la scelta tocca, «Reset camera» torna alla vista di partenza, lo schermo intero allarga
// il palco, e un clic su un oggetto dice quali righe dell'albero lo usano. La pastiglia e i tasti sono
// quelli del palco dello Scene Editor.

const ToolsLayer = styled.div`
    position: absolute;
    inset: 0;
    z-index: 10;
    pointer-events: none;

    > * {
        pointer-events: auto;
    }
`;

const ViewTools = styled(ToolGroup)`
    right: 15px;
    bottom: 15px;
`;

// Il punto del clic: il menu delle righe si apre da qui, come un menu contestuale.
const MenuAnchor = styled.span<{ $x: number; $y: number }>`
    position: absolute;
    top: ${(props) => props.$y}px;
    left: ${(props) => props.$x}px;
    width: 1px;
    height: 1px;
    pointer-events: none;
`;

// Oltre questi pixel fra la pressione e il rilascio è un trascinamento della camera, non un clic.
const CLICK_TOLERANCE = 4;

const ComposerViewportTools: FC<{ stageRef: RefObject<HTMLDivElement | null> }> = ({ stageRef }) => {
    const sceneEditor = usePreview3DStore((store) => store.sceneEditor);
    const [attributes, areas, selectedAttributes, selectedOptions, selectedAreas] = useComposerStore((store) => [
        store.attributes,
        store.areas,
        store.selectedAttributes,
        store.selectedOptions,
        store.selectedAreas
    ]);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [menu, setMenu] = useState<{ x: number; y: number; users: ComposerMeshUser[] } | null>(null);
    const anchorRef = useRef<HTMLSpanElement>(null);
    const [openMenu, closeMenu, isMenuOpen, Menu] = useDropdown();

    const selectionRefs = getSelectionSceneRefs({ attributes, areas, selectedAttributes, selectedOptions, selectedAreas });
    const canFrame = !!sceneEditor && hasSceneRefs(selectionRefs);

    useEffect(() => {
        const onFullscreenChange = () => setIsFullscreen(!!stageRef.current && document.fullscreenElement === stageRef.current);
        document.addEventListener('fullscreenchange', onFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
    }, [stageRef]);

    // INQUADRA CIÒ CHE LA SCELTA TOCCA: le mesh delle sue azioni o delle sue aree. Se nessuna è disegnata
    // (spente, o di un altro modello) la camera resta dov'è.
    const frameSelection = () => {
        const meshes = resolveSceneRefs(sceneEditor, selectionRefs);
        const camera = sceneEditor?.camera as (BABYLON.Camera & { zoomOn?: (meshes: BABYLON.AbstractMesh[], doNotUpdateMaxZ?: boolean) => void }) | null;
        if (!meshes.length || !camera) return;
        if (typeof camera.zoomOn === 'function') camera.zoomOn(meshes, true);
        else sceneEditor?.adjustCamera(true);
    };

    const toggleFullscreen = async () => {
        if (!stageRef.current) return;
        if (document.fullscreenElement) await document.exitFullscreen?.();
        else await stageRef.current.requestFullscreen?.();
    };

    // Scegliere una riga trovata dal 3D è lo stesso gesto dell'albero: poi l'albero la mostra.
    const reveal = (user: ComposerMeshUser) => {
        const store = useComposerStore.getState();
        store.selectAttributes([user.owner]);
        if (user.kind === 'option') {
            store.selectAreas(null);
            store.selectOptions([user.option]);
        } else {
            store.selectOptions(null);
            store.selectAreas([user.area]);
            store.setAttributeFace('areas');
        }
        requestComposerReveal(user.id);
    };

    const revealRef = useRef(reveal);
    revealRef.current = reveal;

    // UN CLIC SU UN OGGETTO, NON UN GIRO DI CAMERA: conta solo un rilascio vicino alla pressione, col
    // tasto sinistro, fuori dai comandi del palco. Ascolta in cattura, così il motore riceve lo stesso
    // gesto di prima e l'orbita non cambia.
    useEffect(() => {
        const stage = stageRef.current;
        if (!stage || !sceneEditor) return;
        let down: { x: number; y: number } | null = null;
        const isOnTools = (event: PointerEvent) => !!(event.target as HTMLElement | null)?.closest('[data-viewport-tools]');
        const onDown = (event: PointerEvent) => {
            down = event.button === 0 && !isOnTools(event) ? { x: event.clientX, y: event.clientY } : null;
        };
        const onUp = (event: PointerEvent) => {
            const start = down;
            down = null;
            if (!start || event.button !== 0 || isOnTools(event)) return;
            if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > CLICK_TOLERANCE) return;
            const canvas = stage.querySelector('canvas');
            const scene = sceneEditor.scene;
            if (!canvas || !scene) return;
            const canvasRect = canvas.getBoundingClientRect();
            const pick = scene.pick(
                event.clientX - canvasRect.left,
                event.clientY - canvasRect.top,
                (mesh) => sceneEditor.isRealMesh(mesh) && mesh.isEnabled() && mesh.isVisible
            );
            const state = useComposerStore.getState();
            // UN CLIC SUL VUOTO TOGLIE LA SCELTA (prototipo), come il clic nel vuoto dell'elenco.
            if (!pick?.hit || !pick.pickedMesh) {
                state.selectAttributes(null);
                state.selectOptions(null);
                state.selectAreas(null);
                return;
            }
            const users = getMeshUsers(pick.pickedMesh, state.attributes, state.areas);
            if (!users.length) return;
            if (users.length === 1) return revealRef.current(users[0]);
            const stageRect = stage.getBoundingClientRect();
            setMenu({ x: event.clientX - stageRect.left, y: event.clientY - stageRect.top, users });
        };
        stage.addEventListener('pointerdown', onDown, true);
        stage.addEventListener('pointerup', onUp, true);
        return () => {
            stage.removeEventListener('pointerdown', onDown, true);
            stage.removeEventListener('pointerup', onUp, true);
        };
    }, [stageRef, sceneEditor]);

    // Il menu si apre quando il punto è disegnato, e si dimentica quando si chiude (Esc, clic fuori).
    const menuWasOpen = useRef(false);
    useEffect(() => {
        if (menu && anchorRef.current) openMenu(anchorRef.current, 'bottom-start');
    }, [menu, openMenu]);
    useEffect(() => {
        if (isMenuOpen) menuWasOpen.current = true;
        else if (menuWasOpen.current) {
            menuWasOpen.current = false;
            setMenu(null);
        }
    }, [isMenuOpen]);

    return (
        <ToolsLayer data-viewport-tools=''>
            <ViewTools>
                <ZTooltip
                    title={canFrame ? T._('Frame the selection', 'Admin') : T._('Select an option, an attribute or an area that uses objects', 'Admin')}
                    position='top'
                >
                    <ToolButton disabled={!canFrame} onClick={frameSelection} aria-label={T._('Frame the selection', 'Admin')}>
                        <FrameSelectionIcon />
                    </ToolButton>
                </ZTooltip>
                <ZTooltip title={T._('Reset camera', 'Admin')} position='top'>
                    <ToolButton disabled={!sceneEditor} onClick={() => sceneEditor?.adjustCamera(true)} aria-label={T._('Reset camera', 'Admin')}>
                        <ResetIcon />
                    </ToolButton>
                </ZTooltip>
                <ZTooltip title={T._(isFullscreen ? 'Exit fullscreen' : 'Fullscreen', 'Admin')} position='top'>
                    <ToolButton
                        $isActive={isFullscreen}
                        onClick={toggleFullscreen}
                        aria-pressed={isFullscreen}
                        aria-label={T._(isFullscreen ? 'Exit fullscreen' : 'Fullscreen', 'Admin')}
                    >
                        {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                    </ToolButton>
                </ZTooltip>
            </ViewTools>
            {menu && <MenuAnchor ref={anchorRef} $x={menu.x} $y={menu.y} />}
            {menu && isMenuOpen && (
                <Menu minWidth='260px' maxHeight='320px'>
                    <DropdownContent
                        onClick={(event) => {
                            event.stopPropagation();
                            closeMenu();
                        }}
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <DropdownList>
                            {menu.users.map((user) => (
                                <DropdownActionItem
                                    key={user.id}
                                    icon={user.kind === 'option' ? <ComposerOptionMark /> : <ComposerAreaMark />}
                                    onClick={() => revealRef.current(user)}
                                >
                                    {user.label}
                                </DropdownActionItem>
                            ))}
                        </DropdownList>
                    </DropdownContent>
                </Menu>
            )}
        </ToolsLayer>
    );
};

export default ComposerViewportTools;
